/* global Word */

// ═══════════════════════════════════════════════════════════════════════
// RUN 27: Non-Breaking Spaces between numbers and units
// ═══════════════════════════════════════════════════════════════════════
//

import { reportResult, paraDetail, RunDetail } from '../runs/common';
import { getConfig } from '../taskpane/configService';

// ───────────────────────────────────────────────────────────────────

/** Escape a string for safe inclusion in a JS regex (literal match). */
function escapeRe(s: string): string {
  // S7780: regex literal for the pattern; String.raw avoids escaping in the replacement
  return s.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

/**
 * Expand a unit string to include both Greek mu (U+03BC) and micro sign
 * (U+00B5) variants. Returns 1 or 2 strings.
 */
function expandMu(unit: string): string[] {
  const GREEK_MU = '\u03BC';
  const MICRO    = '\u00B5';
  if (unit.includes(GREEK_MU)) return [unit, unit.replace(/\u03BC/g, MICRO)];
  if (unit.includes(MICRO))    return [unit, unit.replace(/\u00B5/g, GREEK_MU)];
  return [unit];
}

/**
 * Take the configured list of units, expand μ↔µ, dedupe case-insensitively,
 * and sort by descending length so longest units match first in alternation.
 */
function normalizeUnits(rawUnits: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of rawUnits) {
    if (!u?.trim()) continue;           // S6582: optional chain
    for (const v of expandMu(u.trim())) {
      const key = v.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(v);
      }
    }
  }
  out.sort((a, b) => b.length - a.length);
  return out;
}

/**
 * Build the master detection regex.
 * S7780: String.raw avoids double-escaping in the number part and lookahead.
 */
function buildPattern(units: string[]): RegExp {
  if (units.length === 0) {
    return /(?!)/g;
  }
  const numberPart = String.raw`\d+(?:\.\d+)?`;
  const unitAlt    = units.map(escapeRe).join('|');
  // Lookahead: unit must NOT be followed by another letter, digit, slash,
  // or micro/mu char (which would mean it's part of a longer token).
  const tail = String.raw`(?=[^A-Za-z0-9\u00B5\u03BC/]|$)`;
  return new RegExp(`(${numberPart}) (${unitAlt})${tail}`, 'gi');
}

// ───────────────────────────────────────────────────────────────────

/** Scan a single paragraph's text against the pattern and collect matches. */
function collectMatchesFromText(
  text: string,
  pattern: RegExp,
  matchStrings: Set<string>,
): void {
  if (!text.includes(' ')) return;
  pattern.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    matchStrings.add(m[0]);
  }
}

/** Scan all paragraphs in a collection and collect matches. */
async function collectMatchesFromParas(
  paras: Word.ParagraphCollection,
  pattern: RegExp,
  matchStrings: Set<string>,
  context: Word.RequestContext,
): Promise<void> {
  paras.load('items/text');
  await context.sync();
  for (const para of paras.items) {
    collectMatchesFromText(para.text ?? '', pattern, matchStrings);
  }
}

/** Walk every table cell in the document body and collect matches. */
async function collectMatchesFromTables(
  body: Word.Body,
  pattern: RegExp,
  matchStrings: Set<string>,
  context: Word.RequestContext,
): Promise<void> {
  const tables = body.tables;
  tables.load('items');
  await context.sync();

  for (const table of tables.items) {
    table.rows.load('items');
    await context.sync();
    for (const row of table.rows.items) {
      row.cells.load('items');
      await context.sync();
      for (const cell of row.cells.items) {
        await collectMatchesFromParas(cell.body.paragraphs, pattern, matchStrings, context);
      }
    }
  }
}

/**
 * Replace every occurrence of `matchStr` (ordinary space) with a
 * non-breaking space, recording a detail row for each hit.
 * Returns the number of replacements made.
 */
async function replaceMatchString(
  matchStr: string,
  body: Word.Body,
  changedDetails: RunDetail[],
  context: Word.RequestContext,
): Promise<number> {
  const replacement = matchStr.replace(' ', '\u00A0');

  const hits = body.search(matchStr, { matchCase: true });
  hits.load('items');
  await context.sync();

  if (hits.items.length === 0) {
    console.warn(`  body.search returned no hits for "${matchStr}" — skipped`);
    return 0;
  }

  // Step A — snapshot parent paragraph info before mutating
  const parents = hits.items.map(h => h.paragraphs.getFirst());
  for (const p of parents) p.load('uniqueLocalId, text');
  await context.sync();

  const snapshots = parents.map(p => ({
    paraId:   p.uniqueLocalId ?? '',
    paraText: (p.text ?? '').trim(),
  }));

  console.log(`  "${matchStr}" → ${hits.items.length} hit(s)`);

  // Step B — perform replacements
  for (const hit of hits.items) {
    hit.insertText(replacement, Word.InsertLocation.replace);
  }
  await context.sync();

  // Step C — record detail rows
  for (let i = 0; i < hits.items.length; i++) {
    const snap = snapshots[i];
    if (!snap.paraId) {
      console.warn(`    hit ${i + 1}: no uniqueLocalId — skipping QC row (replacement still applied)`);
      continue;
    }
    changedDetails.push(paraDetail(
      snap.paraId,
      snap.paraText || `(paragraph containing "${matchStr}")`,
      `"${matchStr}" → non-breaking space inserted`,
    ));
  }

  return hits.items.length;
}

// ───────────────────────────────────────────────────────────────────

export async function run27() {
  await Word.run(async (context) => {
    try {
      const cfg = getConfig() as any;
      const r27 = cfg.run27_nonBreakingSpaces || {};
      const rawUnits: string[] = Array.isArray(r27.units) ? r27.units : [];

      const units   = normalizeUnits(rawUnits);
      const pattern = buildPattern(units);

      console.log('═'.repeat(70));
      console.log(`RUN 27: Non-Breaking Spaces (${cfg.clientName || 'default profile'})`);
      console.log('═'.repeat(70));
      console.log('Configured units (after dedupe + μ↔µ expansion, longest first):');
      console.log('  ' + units.join(', '));
      console.log(`Pattern: ${pattern}`);
      console.log('');

      if (units.length === 0) {
        console.warn('[Run 27] No units configured — nothing to do.');
        reportResult('Non-Breaking Spaces — no units configured', [
          { label: 'nonBreakingSpaces', note: 'Nothing to scan' },
        ]);
        return;
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();

      // ── Phase 1: discover all distinct number+unit strings in the document
      const matchStrings = new Set<string>();

      await collectMatchesFromParas(
        context.document.body.paragraphs, pattern, matchStrings, context,
      );
      await collectMatchesFromTables(
        context.document.body, pattern, matchStrings, context,
      );

      console.log(`Phase 1: discovered ${matchStrings.size} distinct number+unit pattern(s)`);
      const distinctList = Array.from(matchStrings);
      for (const ms of distinctList) console.log(`  • "${ms}"`);

      if (matchStrings.size === 0) {
        context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
        await context.sync();
        console.log('No number+unit patterns found — nothing to fix.');
        reportResult('Non-Breaking Spaces — no changes needed', [
          { label: 'Number+unit patterns found in document', count: 0,
            note: 'All measurements already use non-breaking spaces or none present' },
        ]);
        return;
      }

      // ── Phase 2: replace ordinary spaces with non-breaking spaces
      let totalReplacements = 0;
      const changedDetails: RunDetail[] = [];

      for (const matchStr of distinctList) {
        totalReplacements += await replaceMatchString(
          matchStr, context.document.body, changedDetails, context,
        );
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log('');
      console.log('═'.repeat(70));
      console.log(`Distinct patterns: ${matchStrings.size}`);
      console.log(`Total replacements: ${totalReplacements}`);
      console.log('✓ Non-Breaking Spaces complete!');
      console.log('═'.repeat(70));

      const noun = totalReplacements === 1 ? 'replacement' : 'replacements';
      reportResult(`Non-Breaking Spaces — ${totalReplacements} ${noun}`, [
        { label: 'Distinct number+unit patterns found', count: matchStrings.size },
        { label: 'Total non-breaking spaces inserted',  count: totalReplacements },
        ...changedDetails,
      ]);

    } catch (error) {
      console.error('Error in run27:', error);
      console.error('Message:', (error as Error).message);
    }
  });
}